import { useId, useState } from 'react';

import { Button } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { FormField, Textarea } from '@ds/forms';
import { dataEHoraPorExtenso } from '@/comuns/formato';
import { useAviso } from '@/comuns/toast';
import { problemasDaAlteracao, respostasLimpas } from '@/data/fichaImpressa';
import { dados } from '@/data/porta';
import type { FichaPreenchida, RespostaDaFicha } from '@/data/tipos';
import { BlocoDeResposta } from '@/screens/fichas/BlocoDeResposta';

export interface AlterarRespostasModalProps {
    /** Precisa ter a cópia do modelo do dia: é por ela que os campos aparecem. */
    ficha: FichaPreenchida;
    contatoId: string;
    onFechar: () => void;
    onAlterada: () => void;
}

/**
 * Corrigir uma ficha já salva.
 *
 * Os campos são os da CÓPIA do modelo gravada com a ficha, nunca os do modelo
 * de hoje — senão a correção de julho sairia com as perguntas de setembro. O
 * motivo é obrigatório e vai para a auditoria, ao lado do que mudou.
 */
export function AlterarRespostasModal({ ficha, contatoId, onFechar, onAlterada }: AlterarRespostasModalProps) {
    const aviso = useAviso();
    const idMotivo = useId();
    const campos = ficha.definicaoUsada ?? [];
    const [respostas, setRespostas] = useState<Record<string, RespostaDaFicha>>(() => ({ ...ficha.respostas }));
    const [motivo, setMotivo] = useState('');
    const [tentouSalvar, setTentouSalvar] = useState(false);
    const [erro, setErro] = useState<string | null>(null);
    const [salvando, setSalvando] = useState(false);

    const problemas = problemasDaAlteracao(campos, ficha.respostas, respostas, motivo);
    const mudou = JSON.stringify(respostasLimpas(ficha.respostas)) !== JSON.stringify(respostasLimpas(respostas));

    const salvar = async () => {
        setTentouSalvar(true);
        if (problemas.length > 0) return;
        setSalvando(true);
        setErro(null);
        try {
            await dados().alterarRespostasDaFicha(ficha.id, respostasLimpas(respostas), motivo.trim());
            aviso({ title: 'Ficha corrigida', description: `${ficha.nome} · a correção e o motivo ficaram na auditoria` });
            onAlterada();
            onFechar();
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível corrigir a ficha.');
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Modal
            open width={680} onClose={onFechar}
            title={`Corrigir ${ficha.nome}`}
            description={`Preenchida em ${dataEHoraPorExtenso(ficha.criadoEm)}${ficha.preenchidaPor ? ` por ${ficha.preenchidaPor}` : ''}`}
            dirty={mudou || Boolean(motivo.trim())}
            onSave={() => void salvar()} saveLabel="Salvar correção"
            footer={(
                <>
                    <Button variant="secondary" onClick={onFechar}>Cancelar</Button>
                    <Button onClick={() => void salvar()} loading={salvando}>Salvar correção</Button>
                </>
            )}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <Alert tone="warning" title="Correção de prontuário" dismissible={false}>
                    O que mudou, quem mudou e o motivo ficam na auditoria. Os campos são os do dia do preenchimento — o modelo de hoje não entra aqui.
                </Alert>
                {erro && <Alert tone="danger" title="Não foi possível corrigir" dismissible={false}>{erro}</Alert>}
                {tentouSalvar && problemas.length > 0 && (
                    <Alert tone="danger" title="Falta resolver" dismissible={false}>{problemas.join(' ')}</Alert>
                )}

                {campos.map((campo) => (
                    <FormField key={campo.uid} label={campo.rotulo} required={campo.obrigatorio}>
                        <BlocoDeResposta
                            campo={campo}
                            valor={respostas[campo.uid]}
                            onResponder={(v) => setRespostas((atuais) => ({ ...atuais, [campo.uid]: v }))}
                            onEnviarArquivo={(arquivo) => dados().enviarAnexoDaFicha(contatoId, campo.uid, arquivo)}
                            onAbrirArquivo={(caminho) => dados().enderecoDoAnexo(caminho)}
                        />
                    </FormField>
                ))}

                <FormField label="Motivo da correção" htmlFor={idMotivo} required hint="Vai para a auditoria do prontuário">
                    <Textarea id={idMotivo} rows={2} value={motivo} onChange={setMotivo} placeholder="Ex.: queixa registrada com a data errada" />
                </FormField>
            </div>
        </Modal>
    );
}
